import { useState, useId } from "react";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon } from "iconsax-react";
import cn from "classnames";
import { type Locale } from "@/shared/utils";
import { useHighlightFade } from "@/shared/hooks/useHighlightFade";
import { CloseIcon } from "@/shared/assets/icons";
import Dropdown from "../Dropdown";
import Calendar from "./Calendar";
import type { HighlightedRange } from "./CalendarDays";
import { useCalendar, type RangeValue } from "./useCalendar";
import styles from "./DatePicker.module.css";

export type DatePickerMode = "single" | "range";

export type { RangeValue };
export type { HighlightedRange };

interface BaseDatePickerProps {
  label?: string;
  error?: string;
  placeholder?: string;
  locale: Locale;
  disabled?: boolean;
  hasTime?: boolean;
  direction?: "bottom" | "top";
  minDate?: Date;
  maxDate?: Date;
  hasRequiredLabel?: boolean;
  isSmall?: boolean;
  icon?: React.ReactNode;
  width?: string;
  align?: "left" | "right";
  fullWidth?: boolean;
  hasPeriodPicker?: boolean;
  clearable?: boolean;
  highlightedRanges?: HighlightedRange[];
  highlight?: boolean;
}

type SingleDateProps = BaseDatePickerProps & {
  mode?: "single";
  value?: Date | null;
  onChange?: (v: Date | null) => void;
  formatDisplayValue?: never;
};

type RangeDateProps = BaseDatePickerProps & {
  mode: "range";
  value?: RangeValue | null;
  onChange?: (v: RangeValue | null) => void;
  formatDisplayValue?: (range: RangeValue) => string;
};

export type DatePickerProps = SingleDateProps | RangeDateProps;

export default function DatePicker(props: DatePickerProps) {
  const {
    label,
    error,
    mode = "single",
    value,
    onChange,
    placeholder,
    locale,
    disabled = false,
    hasTime = false,
    direction = "bottom",
    minDate,
    maxDate,
    hasRequiredLabel = false,
    isSmall = false,
    icon,
    width = "w-full",
    align,
    fullWidth = false,
    hasPeriodPicker = false,
    clearable = false,
    highlightedRanges,
    highlight = false,
  } = props;

  const formatDisplayValue = props.mode === "range" ? (props as RangeDateProps).formatDisplayValue : undefined;

  const { t } = useTranslation("Common");
  const resolvedPlaceholder = placeholder ?? t("datePicker.placeholder");
  const showHighlight = useHighlightFade(highlight) && !error;

  const [open, setOpen] = useState(false);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [pendingRange, setPendingRange] = useState<RangeValue | null>(null);
  const inputId = useId();

  const isRange = mode === "range";
  const effectivePeriodPicker = hasPeriodPicker && isRange;

  const safeRange: RangeValue = isRange
    ? ((value as RangeValue | null) ?? { start: null, end: null })
    : { start: (value as Date | null) ?? null, end: null };

  const cal = useCalendar(
    value as RangeValue | Date | null | undefined,
    handleCalendarChange,
    mode,
    hasTime,
    effectivePeriodPicker,
  );

  function handleClose() {
    setOpen(false);
    setHoverDate(null);
    setPendingRange(null);
    if (effectivePeriodPicker) {
      cal.reset(value as RangeValue | Date | null);
    }
  }

  function handleCalendarChange(v: RangeValue | Date | null) {
    if (disabled) return;

    if (isRange && effectivePeriodPicker && cal.isInFreeRange) {
      const range = v as RangeValue | null;
      if (range) {
        setPendingRange(range);
        if (range.start && range.end) {
          (onChange as ((v: RangeValue | null) => void) | undefined)?.(range);
          setPendingRange(null);
          handleClose();
        }
      }
      return;
    }

    if (mode === "single") {
      const dateValue =
        v instanceof Date ? v : v && typeof v === "object" && "start" in v ? (v as RangeValue).start : null;
      (onChange as ((v: Date | null) => void) | undefined)?.(dateValue);
    } else {
      const rangeValue = v && typeof v === "object" && "start" in v ? (v as RangeValue) : null;
      (onChange as ((v: RangeValue | null) => void) | undefined)?.(rangeValue);
    }

    if (!hasTime) {
      if (mode === "single") handleClose();

      if (mode === "range" && v && typeof v === "object" && "end" in v && (v as RangeValue).end) {
        handleClose();
      }
    }
  }

  const isPlaceholder = (isRange && !safeRange.start && !safeRange.end) || (!isRange && !(value as Date | null));
  const showClear = clearable && !isPlaceholder && !disabled;

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    if (mode === "single") {
      (onChange as ((v: Date | null) => void) | undefined)?.(null);
    } else {
      (onChange as ((v: RangeValue | null) => void) | undefined)?.(null);
    }
    setOpen(false);
  }

  function formatValue() {
    if (isPlaceholder) return resolvedPlaceholder;

    if (isRange && formatDisplayValue && safeRange.start && safeRange.end) {
      return formatDisplayValue(safeRange);
    }

    function getLocaleString(loc: Locale): string {
      const localeMap: Record<Locale, string> = {
        ru: "ru-RU",
        kk: "kk-KZ",
        en: "en-US",
      };
      return localeMap[loc];
    }

    function formatDateTime(date: Date) {
      if (!hasTime) return date.toLocaleDateString(getLocaleString(locale));

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    }

    if (!isRange) {
      const v = value as Date | null;
      return v ? formatDateTime(v) : resolvedPlaceholder;
    }

    const { start, end } = safeRange;
    if (start && !end) return formatDateTime(start);
    return `${start ? formatDateTime(start) : ""} — ${end ? formatDateTime(end) : ""}`;
  }

  const calendarValue =
    effectivePeriodPicker && cal.isInFreeRange && pendingRange ? pendingRange : safeRange;

  return (
    <Dropdown
      open={open && !disabled}
      onClose={handleClose}
      direction={direction}
      align={align}
      fullWidth={fullWidth}
      className="elevation-level-2!">
      <div className={width}>
        <div className="flex flex-col gap-2">
          {label && (
            <div className="flex items-center gap-1">
              <label
                htmlFor={inputId}
                className={cn("text-label-sm", disabled ? "content-base-low" : "content-base-primary")}>
                {label}
              </label>
              {hasRequiredLabel && <span className="text-label-sm content-action-negative">*</span>}
            </div>
          )}
          <button
            id={inputId}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (!disabled) setOpen((o) => !o);
            }}
            className={cn(
              "w-full flex items-center px-3 gap-2",
              "surface-base-fill input-box-shadow text-body-regular-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-200",
              isSmall ? "h-8 radius-xs" : "py-3 radius-sm",
              showHighlight && "input-highlight-box-shadow",
              disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
            )}>
            {icon && <span className="shrink-0 flex items-center text-grey-400">{icon}</span>}
            <span
              className={cn(
                "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left",
                isPlaceholder ? styles.placeholder : "content-base-secondary",
              )}>
              {formatValue()}
            </span>
            {!icon && (showClear ? (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClear(e as unknown as React.MouseEvent); }}
                className="content-base-low hover:content-base-secondary transition-colors w-4 h-4 flex items-center justify-center shrink-0 cursor-pointer">
                <CloseIcon size={14} />
              </span>
            ) : (
              <span className="content-base-secondary w-4 h-4 flex items-center justify-center shrink-0">
                <CalendarIcon size={16} color="currentColor" style={{ width: "1rem", height: "1rem" }} />
              </span>
            ))}
          </button>
          {error && !disabled && <p className="text-body-regular-xs content-action-negative">{error}</p>}
        </div>
      </div>

      {!disabled && (
        <Calendar
          {...cal}
          value={calendarValue}
          hoverDate={isRange ? hoverDate : null}
          onHover={isRange ? setHoverDate : undefined}
          locale={locale}
          mode={mode}
          hasTime={hasTime}
          minDate={minDate}
          maxDate={maxDate}
          hasPeriodPicker={effectivePeriodPicker}
          highlightedRanges={highlightedRanges}
          onChange={(v) => {
            if (mode === "single") {
              const dateValue =
                v instanceof Date ? v : v && typeof v === "object" && "start" in v ? (v as RangeValue).start : null;
              (onChange as ((v: Date | null) => void) | undefined)?.(dateValue);
            } else {
              const rangeValue = v && typeof v === "object" && "start" in v ? (v as RangeValue) : null;
              (onChange as ((v: RangeValue | null) => void) | undefined)?.(rangeValue);
            }
          }}
          onClose={hasTime ? handleClose : undefined}
        />
      )}
    </Dropdown>
  );
}
